import React, { useState, useEffect } from "react";
import NoreaElfico from "../assets/images/norea-elfico.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlug, faTachometerAlt, faUsers, faTasks, faChartBar, faCog,
  faQuestionCircle, faSignOutAlt, faDollarSign, faTags, faBrain
} from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faWhatsapp, faMeta, faGoogle } from "@fortawesome/free-brands-svg-icons";
import styled, { css, createGlobalStyle } from "styled-components";
import { Link, useNavigate } from "react-router-dom";

const SIDEBAR_EXPANDED = 250;
const SIDEBAR_COLLAPSED = 72;

/* ⬇️ CSS global para o main se adaptar à largura da sidebar no hover */
const GlobalAdapt = createGlobalStyle`
  /* padrão: sem sidebar (login etc.) */
  .main-content {
    margin-left: 0;
    transition: margin-left 180ms ease;
  }

  /* quando há sidebar (app autenticado) */
  .has-sidebar .main-content {
    margin-left: ${SIDEBAR_COLLAPSED}px;
  }

  /* ao passar o mouse sobre a sidebar, expande e empurra o conteúdo */
  .has-sidebar .app-sidebar:hover ~ .main-content {
    margin-left: ${SIDEBAR_EXPANDED}px;
  }
`;


const Sidebar = styled.aside.attrs({ className: "app-sidebar" })`
  width: ${SIDEBAR_COLLAPSED}px;
  height: 100vh;
  background-color: #232332;
  color: #fff;
  display: flex;
  flex-direction: column;
  padding: 20px 16px;
  position: fixed;
  left: 0;
  top: 0;
  transition: width 180ms ease;
  overflow: hidden;
  z-index: 100;

  &::after {
    content: "";
    position: absolute;
    top: 0; right: 0;
    width: 5px; height: 100%;
    background-color: #2f2f41;
  }

  /* Hover = expandida */
  &:hover { width: ${SIDEBAR_EXPANDED}px; }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 6px 0 16px;
`;

const Logo = styled.img`
  width: 40px;
  margin: 8px 0;
  transition: width 180ms ease, margin 180ms ease;
  ${Sidebar}:hover & {
    width: 70px;
    margin: 30px 0 10px 10px;
  }
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 10px;
  cursor: pointer;
  color: ${(props) => (props.$expanded ? "#FFD700" : "#FFF")};
  background: ${(props) => (props.$expanded ? "#2f2f41" : "transparent")};
  transition: background-color 0.2s, color 0.2s, gap 180ms ease;
  border-radius: 8px;
  white-space: nowrap;
  justify-content: center;      /* fechado = ícones centralizados */

  &:hover { background-color: #2f2f41; }

  /* aberto (hover do sidebar) = mostra texto e dá espaço para o ícone */
  ${Sidebar}:hover & {
    gap: 10px;
    justify-content: flex-start;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  font-size: 18px;
`;

const MenuTitle = styled.span`
  display: none;
  font-size: 16px;
  color: #fff;

  ${Sidebar}:hover & {
    display: inline;            /* só aparece com sidebar expandida */
  }
`;

const SubMenu = styled.div`
  margin-left: 20px;
  display: none;                /* fechado = escondido */
  transition: all 0.2s;

  a {
    color: #FFF !important;
    text-decoration: none !important;
    font-size: 14px;
  }
  a:hover { color: #FFD700 !important; }

  /* só mostra submenus quando a sidebar está expandida */
  ${Sidebar}:hover & {
    display: ${(props) => (props.$show ? "block" : "none")};
  }
`;

function MenuLateral({ handleLogout }) {
  const [activeMenu, setActiveMenu] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [usuarioId, setUsuarioId] = useState(null);
  const [whatsappAtivo, setWhatsappAtivo] = useState(false);
  const [iaAtiva, setIaAtiva] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const dataStr = localStorage.getItem("usuario");
    if (dataStr) {
      const data = JSON.parse(dataStr);
      setWhatsappAtivo(data.whatsapp_ativo === 1);
      setIaAtiva(data.ia_ativa === 1);
      setUsuarioId(Number(data.id));
      setIsAdmin(data.tipo_usuario === "master");
    }
  }, []);

  const toggleMenu = (menu) => setActiveMenu(activeMenu === menu ? "" : menu);

  return (
    <>
      <GlobalAdapt />
      <Sidebar>
        <TopRow>
          <Logo src={NoreaElfico} alt="Norea Logo" />
        </TopRow>

        {/* Dashboard */}
        <MenuItem onClick={() => navigate("/dashboard")}>
          <Icon icon={faTachometerAlt} />
          <MenuTitle>Dashboard</MenuTitle>
        </MenuItem>

        {/* Leads */}
        <MenuItem onClick={() => toggleMenu("leads")} $expanded={activeMenu === "leads"}>
          <Icon icon={faTasks} />
          <MenuTitle>Leads</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "leads"}>
          <MenuItem><Link to="/cadastro-lead">Cadastrar Lead</Link></MenuItem>
          <MenuItem><Link to="/listagem-leads">Listagem de Leads</Link></MenuItem>
        </SubMenu>

        {/* Ações */}
        <MenuItem onClick={() => toggleMenu("actions")} $expanded={activeMenu === "actions"}>
          <Icon icon={faTasks} />
          <MenuTitle>Ações</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "actions"}>
          <MenuItem><Link to="/acoes">Listagem de Ações</Link></MenuItem>
          <MenuItem><Link to="/criar-acao">Criar Ação / Objeção</Link></MenuItem>
          <MenuItem><Link to="/revisao-objecoes">Revisar Objeções</Link></MenuItem>
        </SubMenu>

        {/* Tags */}
        <MenuItem onClick={() => toggleMenu("tags")} $expanded={activeMenu === "tags"}>
          <Icon icon={faTags} />
          <MenuTitle>Tags</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "tags"}>
          <MenuItem><Link to="/gerenciar-tags">Gerenciar Tags</Link></MenuItem>
        </SubMenu>

        {/* Usuários (Admin) */}
        {isAdmin && usuarioId !== 80 && (
          <>
            <MenuItem onClick={() => toggleMenu("users")} $expanded={activeMenu === "users"}>
              <Icon icon={faUsers} />
              <MenuTitle>Usuários</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "users"}>
              <MenuItem><Link to="/gerenciar-usuarios">Lista de Usuários</Link></MenuItem>
              <MenuItem><Link to="/adicionar-usuario">Criar Usuário</Link></MenuItem>
              <MenuItem><Link to="/usuario-config">Configurações de Usuário</Link></MenuItem>
              <MenuItem><Link to="/openai/iarecomendacoes-meta">Recomendações Meta</Link></MenuItem>
            </SubMenu>

            {/* Financeiro */}
            <MenuItem onClick={() => toggleMenu("financeiro")} $expanded={activeMenu === "financeiro"}>
              <Icon icon={faDollarSign} />
              <MenuTitle>Financeiro</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "financeiro"}>
              <MenuItem><Link to="/wzp/painel-mensagens-teste">TESTE</Link></MenuItem>
              <MenuItem><Link to="/financeiro/calculo-comissoes">Comissões</Link></MenuItem>
              <MenuItem><Link to="/listar-cobrancas">Listar Cobranças</Link></MenuItem>
              <MenuItem><Link to="/financeiro/listar-data-cobranca">Data Cobrança</Link></MenuItem>
              <MenuItem><Link to="/financeiro/associar-pagamento">Associação Pendentes</Link></MenuItem>
              <MenuItem><Link to="/financeiro/previsao-recebimentos">Previsão</Link></MenuItem>
              <MenuItem><Link to="/financeiro/notas">Notas Fiscais</Link></MenuItem>
              <MenuItem><Link to="/financeiro/saldo-asaas">Saldo</Link></MenuItem>
            </SubMenu>


        {/* Configurar WhatsApp */}
        <MenuItem onClick={() => toggleMenu("config-wzp")} $expanded={activeMenu === "config-wzp"}>
          <Icon icon={faPlug} />
          <MenuTitle>Configurar WhatsApp</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "config-wzp"}>
          <MenuItem><Link to="/wzp/painel-instancias">Painel Instâncias</Link></MenuItem>
        </SubMenu>

                {/* Google Ads Admin */}
        <MenuItem
          onClick={() => toggleMenu("google-adm")}
          $expanded={activeMenu === "google-adm"}
        >
          <Icon icon={faGoogle} />
          <MenuTitle>Config Google</MenuTitle>
        </MenuItem>

        <SubMenu $show={activeMenu === "google-adm"}>
          <MenuItem><Link to="/googleads/integracao-googleads">Vincular Contas</Link></MenuItem>
          <MenuItem><Link to="/googleads/relatorio-googleads">Relatorio Contas</Link></MenuItem>

        </SubMenu>

        {/* Meta Admin */}
        <MenuItem onClick={() => toggleMenu("meta-adm")} $expanded={activeMenu === "meta-adm"}>
          <Icon icon={faFacebook} />
          <MenuTitle>Config Meta</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "meta-adm"}>
          <MenuItem><Link to="/meta/vincular-contas-meta">Vincular Contas</Link></MenuItem>
        </SubMenu>
          </>
        )}


        {/* Meta Usuário */}
        <MenuItem onClick={() => toggleMenu("meta-user")} $expanded={activeMenu === "meta-user"}>
          <Icon icon={faMeta} />
          <MenuTitle>Meta</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "meta-user"}>
          <MenuItem><Link to="/meta/relatorio-meta">Relatórios</Link></MenuItem>
          <MenuItem><Link to="/meta/saldo-meta">Saldo</Link></MenuItem>
        </SubMenu>


        {/* WhatsApp */}
        {whatsappAtivo && (
          <>
            <MenuItem onClick={() => toggleMenu("whatsapp")} $expanded={activeMenu === "whatsapp"}>
              <Icon icon={faWhatsapp} />
              <MenuTitle>WhatsApp</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "whatsapp"}>
              <MenuItem><Link to="/wzp/painel-mensagens">Mensagens</Link></MenuItem>
              <MenuItem><Link to="/wzp/respostas-rapidas">Respostas Rápidas</Link></MenuItem>
              <MenuItem><Link to="/wzp/fila-mensagens">Fila de Mensagens</Link></MenuItem>
              <MenuItem><Link to="/wzp/painel-whatsapp">Conectar</Link></MenuItem>
              <MenuItem><Link to="/funil/painel-funil">Funil WhatsApp</Link></MenuItem>
            </SubMenu>
          </>
        )}

        {/* OpenAI (IA) */}
        {iaAtiva && (
          <>
            <MenuItem onClick={() => toggleMenu("openai")} $expanded={activeMenu === "openai"}>
              <Icon icon={faBrain} />
              <MenuTitle>OpenAI</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "openai"}>
              <MenuItem><Link to="/openai/painel">Painel da IA</Link></MenuItem>
            </SubMenu>
          </>
        )}

        {/* Dados */}
        <MenuItem onClick={() => toggleMenu("reports")} $expanded={activeMenu === "reports"}>
          <Icon icon={faChartBar} />
          <MenuTitle>Dados</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "reports"}>
          <MenuItem><Link to="/relatorios">Relatórios</Link></MenuItem>
          <MenuItem><Link to="/exportacao-dados">Exportar Dados</Link></MenuItem>
        </SubMenu>

        {/* Configurações */}
        <MenuItem onClick={() => toggleMenu("settings")} $expanded={activeMenu === "settings"}>
          <Icon icon={faCog} />
          <MenuTitle>Configurações</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "settings"}>
          <MenuItem><Link to="/alterar-senha">Alterar Senha</Link></MenuItem>
          <MenuItem><Link to="/meus-dados">Meus Dados</Link></MenuItem>
        </SubMenu>

        {/* Ajuda */}
        <MenuItem onClick={() => toggleMenu("help")} $expanded={activeMenu === "help"}>
          <Icon icon={faQuestionCircle} />
          <MenuTitle>Ajuda</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "help"}>
          <MenuItem><Link to="/ajuda-suporte">Ajuda e Suporte</Link></MenuItem>
          <MenuItem><Link to="/opiniao">Opinião do Usuário</Link></MenuItem>
        </SubMenu>

        {/* Logout */}
        <MenuItem onClick={handleLogout}>
          <Icon icon={faSignOutAlt} />
          <MenuTitle>Sair</MenuTitle>
        </MenuItem>
      </Sidebar>
    </>
  );
}

export default MenuLateral;
